'use client';

import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import DeliveryTruckIcon from '@/components/icons/DeliveryTruckIcon';
import MarkerPinIcon from '@/components/icons/MarkerPinIcon';
import { Button } from '@/components';
import {
  DANK_OPEN_DELIVERY_POSTAL_MODAL,
} from '@/lib/header-events';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

type DeliveryTerms = {
  sameDayFee: number;
  sameDayFreeDeliveryThreshold: number;
  mailOrderFee: number;
  mailOrderFreeShippingThreshold: number;
  deliveryOpenTime: string | null;
  deliveryCloseTime: string | null;
};

/** Values shipped in the bundle, used until the live ones arrive. */
const FALLBACK_TERMS: DeliveryTerms = {
  sameDayFee: 9.99,
  sameDayFreeDeliveryThreshold: 100,
  mailOrderFee: 14.99,
  mailOrderFreeShippingThreshold: 199,
  deliveryOpenTime: '10:00',
  deliveryCloseTime: '23:00',
};

const money = (n: number) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);

/** "10:00" -> "10AM", "23:00" -> "11PM" */
function hour12(value: string | null): string {
  const [h] = (value || '').split(':');
  const n = Number(h);
  if (!Number.isFinite(n)) return '';
  const suffix = n >= 12 ? 'PM' : 'AM';
  const display = n % 12 === 0 ? 12 : n % 12;
  return `${display}${suffix}`;
}

/**
 * The table used to hardcode every number in this comparison, and they had
 * already drifted from checkout: it advertised free same-day delivery over $99
 * while the fee engine charged until $100, so a $99.50 order was promised free
 * delivery and then billed $9.99. These come from /api/delivery-terms now, which
 * reads the same config the checkout does.
 */
function buildRows(t: DeliveryTerms): { feature: string; sameDay: React.ReactNode; mailOrder: React.ReactNode }[] {
  const open = hour12(t.deliveryOpenTime);
  const close = hour12(t.deliveryCloseTime);
  return [
  {
    feature: 'Coverage',
    sameDay: 'Calgary, Chestermere, Okotoks, High River, Aldersyde',
    mailOrder: (
      <>
        <strong>All Alberta</strong> addresses<br />(Province-wide)
      </>
    ),
  },
  {
    feature: 'Speed',
    sameDay: (
      <>As little as <strong>45 mins</strong> (On-demand)</>
    ),
    mailOrder: (
      <>Shipped <strong>same business day</strong> (via) Canada Post</>
    ),
  },
  {
    feature: 'Hours / Cut-off',
    sameDay: open && close ? `Daily: ${open} - ${close}` : 'Daily',
    mailOrder: (
      <>Daily Cut-off: <strong>3PM MST</strong></>
    ),
  },
  {
    feature: 'Tracking',
    sameDay: 'Real-time SMS & Live map',
    mailOrder: 'Canada Post tracking number',
  },
  {
    feature: 'Delivery Fee',
    sameDay: money(t.sameDayFee),
    mailOrder: `${money(t.mailOrderFee)} (Xpresspost)`,
  },
  {
    feature: 'Free Shipping',
    sameDay: (
      <>On orders <strong>{money(t.sameDayFreeDeliveryThreshold)}+</strong></>
    ),
    mailOrder: (
      <>On orders <strong>{money(t.mailOrderFreeShippingThreshold)}+</strong></>
    ),
  },
  {
    feature: 'Min. Purchase',
    sameDay: '$50',
    mailOrder: '$50',
  },
  ];
}

function openDeliveryPostalModal() {
  window.dispatchEvent(new CustomEvent(DANK_OPEN_DELIVERY_POSTAL_MODAL));
}

export default function DeliveryComparisonSection() {
  const [terms, setTerms] = useState<DeliveryTerms>(FALLBACK_TERMS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/delivery-terms`, { cache: 'no-store' });
        const data = await res.json();
        if (!cancelled && data?.success) setTerms({ ...FALLBACK_TERMS, ...data });
      } catch {
        // Bundled values stand in; they match the shipped config.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const ROWS = useMemo(() => buildRows(terms), [terms]);
  return (
    <section
      className="w-full px-4 sm:px-6 lg:px-8"
      aria-label="Choose Your Delivery Way"
    >
      <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">

        {/* Left: heading + description + CTAs */}
        <div className="flex flex-col gap-6">
          <div>
            <h2 className="text-h3-mobile sm:text-h3-desktop font-bold text-text-primary leading-tight">
              Choose Your Delivery Way
            </h2>
            <p className="mt-3 text-body-16-desktop text-text-secondary leading-relaxed">
              Not sure which option fits your schedule? Compare our Same-Day Delivery for local
              speed versus Mail Order for province-wide coverage. Check the fees, times, and
              minimums below to decide
            </p>
          </div>

          <div className="flex max-sm:flex-col gap-3">
            <Button
              variant="primary"
              size="lg"
              onClick={openDeliveryPostalModal}
              className="rounded-xl"
            >
              Order delivery
            </Button>
            <Button
              asChild
              variant="primary"
              size="lg"
              className="rounded-xl"
            >
              <Link href="/search?mailOrder=true">Shop Mail Order</Link>
            </Button>
          </div>
        </div>

        {/* Right: comparison table, on small screens, first column stays sticky while horizontal scrolls (feature labels stay visible). */}
        <div className="relative w-full overflow-hidden rounded-2xl border border-neutral-10 max-lg:bg-neutral-5 lg:bg-surface-primary">
          {/* Background decorative shape */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden>
            <Image
              src="/media/pages/home/cta/banner-shopping-background-shape.webp"
              alt=""
              fill
              className="object-cover opacity-30"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className="relative z-10 overflow-x-auto">
            <table className="w-full min-w-[480px] text-left border-collapse">
              <thead>
                <tr className="border-b border-neutral-10">
                  <th
                    scope="col"
                    className="py-4 pl-5 pr-3 text-body-sm font-semibold text-text-secondary whitespace-nowrap border-r border-neutral-10 max-lg:bg-neutral-5 lg:bg-transparent lg:static max-lg:sticky max-lg:left-0 max-lg:z-[2] max-lg:shadow-[4px_0_12px_-8px_rgba(0,0,0,0.2)]"
                  >
                    Feature
                  </th>
                  <th
                    scope="col"
                    className="py-4 px-4 text-body-sm font-semibold text-text-primary text-center border-r border-neutral-10 max-lg:bg-neutral-5 lg:bg-transparent"
                  >
                    <span className="inline-flex items-center gap-1.5 justify-center">
                      <DeliveryTruckIcon size={15} color="currentColor" className="text-blue-80 shrink-0" />
                      Same-Day Delivery
                    </span>
                  </th>
                  <th scope="col" className="py-4 pl-4 pr-5 text-body-sm font-semibold text-text-primary text-center max-lg:bg-neutral-5 lg:bg-transparent">
                    <span className="inline-flex items-center gap-1.5 justify-center">
                      <MarkerPinIcon size={15} color="currentColor" className="text-blue-80 shrink-0" />
                      Mail Order Marijuana
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row, i) => (
                  <tr
                    key={row.feature}
                    className={i < ROWS.length - 1 ? 'border-b border-neutral-10' : ''}
                  >
                    <th
                      scope="row"
                      className="py-4 pl-5 pr-3 text-left text-body-sm font-semibold text-text-secondary align-middle whitespace-nowrap border-r border-neutral-10 max-lg:bg-neutral-5 lg:bg-transparent lg:static max-lg:sticky max-lg:left-0 max-lg:z-[1] max-lg:shadow-[4px_0_12px_-8px_rgba(0,0,0,0.15)]"
                    >
                      {row.feature}
                    </th>
                    <td className="py-4 px-4 text-body-sm text-text-secondary align-middle text-center border-r border-neutral-10 max-lg:bg-neutral-5 lg:bg-transparent">
                      {row.sameDay}
                    </td>
                    <td className="py-4 pl-4 pr-5 text-body-sm text-text-secondary align-middle text-center max-lg:bg-neutral-5 lg:bg-transparent">
                      {row.mailOrder}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </section>
  );
}
